import { Edit, SimpleForm, NumberInput, SelectInput, TextInput, useGetList, useRecordContext } from 'react-admin';
import { Box, Typography, CircularProgress } from '@mui/material';
import SupplierInventoryBreakdown from '../components/SupplierInventoryBreakdown';

const parseOptions = (options) => {
  if (!options) return [];
  if (Array.isArray(options)) return options.map(o => ({ id: String(o).trim(), name: String(o).trim() }));
  if (typeof options === 'string') return options.split(',').map(o => ({ id: o.trim(), name: o.trim() })).filter(o => o.id);
  return [];
};

const DynamicFields = ({ productId, existingAttrs }) => {
  const { data: attributes = [], isLoading } = useGetList('attributes', {
    filter: { product_id: productId },
    pagination: { page: 1, perPage: 100 },
    sort: { field: 'id', order: 'ASC' },
  });

  if (isLoading) return <CircularProgress size={20} />;
  if (!attributes.length) return (
    <Typography variant="body2" color="text.secondary">
      אין תכונות מוגדרות למוצר זה.
    </Typography>
  );

  return (
    <>
      {attributes.map(attr => {
        const choices = parseOptions(attr.options);
        const defaultVal = existingAttrs?.[attr.key] ?? '';
        return choices.length > 0 ? (
          <SelectInput
            key={attr.id}
            source={`attr_${attr.key}`}
            label={attr.label || attr.key}
            choices={choices}
            defaultValue={defaultVal}
            fullWidth
          />
        ) : (
          <TextInput
            key={attr.id}
            source={`attr_${attr.key}`}
            label={attr.label || attr.key}
            defaultValue={defaultVal}
            fullWidth
          />
        );
      })}
    </>
  );
};

const VariantEditForm = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <SimpleForm>
      <TextInput source="name" label="שם המוצר (למשל: תפילין גסות אשכנזי גודל 7)" fullWidth />

      <Box sx={{ width: '100%', mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          תכונות המוצר
        </Typography>
        <DynamicFields
          productId={record.product_id}
          existingAttrs={record.attributes}
        />
      </Box>
      <Box className="crm-form-grid" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 2, width: '100%' }}>
        <NumberInput source="price_ils" label="מחיר ₪" min={0} fullWidth />
        <NumberInput source="price_usd" label="מחיר $"  min={0} fullWidth />
        <NumberInput source="quantity" label="כמות במלאי" min={0} step={1}
          format={v => (v != null && v !== '') ? Math.round(Number(v)) : ''}
          parse={v => (v !== '' && v != null) ? Math.round(Number(v)) : null}
          fullWidth />
        <NumberInput source="min_stock" label="מלאי מינימום (התראה)" min={0} step={1}
          format={v => (v != null && v !== '') ? Math.round(Number(v)) : ''}
          parse={v => (v !== '' && v != null) ? Math.round(Number(v)) : null}
          fullWidth helperText="ריק = ללא התראה" />
      </Box>

      <Box sx={{ width: '100%', mt: 1 }}>
        <SupplierInventoryBreakdown variantId={record.id} />
      </Box>
    </SimpleForm>
  );
};

const VariantEdit = () => {
  const transform = (data) => {
    const attributes = {};
    const clean = {};
    for (const [key, value] of Object.entries(data)) {
      if (key.startsWith('attr_')) {
        attributes[key.slice(5)] = value;
      } else {
        clean[key] = value;
      }
    }
    return { ...clean, attributes };
  };

  return (
    <Edit transform={transform} title="עריכת וריאנט">
      <VariantEditForm />
    </Edit>
  );
};

export default VariantEdit;
